/** GENERATED_ASSET — foam/ripple layer and optional spray layer. Static art is finished without spray. */
import type { Ctx } from './ctx';
import type { Layer } from './svgKit';
import { Path, arcPoints } from './pathKit';
import type { Pt } from './pathKit';
import { waterCut } from './layout';
import type { TierLayout } from './layout';

/** Where fall k visibly lands. The bottom tier lands in the basin; others on the slab below. */
export function landingPoint(t: TierLayout): Pt {
  return t.k === 0 ? { x: t.lipX, y: t.base + 1.8 } : { x: t.lipX, y: t.base + 3.4 };
}

function foamCluster(ctx: Ctx, L: Layer, t: TierLayout): void {
  const { sw, p } = ctx;
  const c = landingPoint(t);
  const r = ctx.rng(`foam.cluster.${t.k}`);
  const big = t.k === 0;
  const n = 3 + Math.round(p.flow * 3);
  const spread = (big ? 10 : 7) * (0.7 + 0.4 * p.flow);
  for (let i = 0; i < 6; i++) {
    const dx = r.range(-spread, spread), rx = r.range(2.6, big ? 6.4 : 5), ry = r.range(1.4, big ? 2.6 : 2.2), dy = r.range(-0.4, 0.8);
    if (i >= n) continue;
    L.ellipse(c.x + dx, c.y + dy, rx, ry, { fill: sw.water.foam, stroke: sw.water.foamShade, sw: 0.8, so: 0.8, part: `foam${t.k}` });
  }
  L.ellipse(c.x, c.y - 0.2, spread * 0.55, big ? 2 : 1.6, { fill: sw.water.foam, part: 'foam-core' });
}

/** Arcs in front of the splash, trimmed to stay inside the visible water. */
function rippleRings(ctx: Ctx, L: Layer): void {
  const { L: lay, sw, p } = ctx;
  const w = lay.water, t0 = lay.tiers[0]!;
  const c = landingPoint(t0);
  const grow = ctx.phase === undefined ? 0 : ctx.phase * 0.22;
  const rings = [[16, 1.7], [31, 2.7], [49, 3.5]] as const;
  rings.forEach(([rx0, ry0], i) => {
    const rx = rx0 * (1 + grow), ry = Math.min(ry0 * (1 + grow), w.y1 - 1 - c.y);
    if (ry < 0.6) return;
    const pts = arcPoints(c.x, c.y, rx, ry, 6, 174, 30).filter((q) => q.x > w.x0 + 4 && q.x < w.x1 - 4 && q.y < w.y1 - 0.7);
    if (pts.length < 3) return;
    const so = (0.95 - i * 0.2) * (0.35 + 0.65 * p.flow) * (ctx.phase === undefined ? 1 : 1 - ctx.phase * 0.35);
    L.path(new Path().poly(pts, false), { stroke: sw.water.sheetLight, sw: 1.3 - i * 0.15, so, part: `ripple-ring${i}` });
  });
}

function rippleMarks(ctx: Ctx, L: Layer): void {
  const { L: lay, sw, p } = ctx;
  const w = lay.water, cut = waterCut(lay);
  const r = ctx.rng('foam.ripples');
  const N = 10;
  const n = Math.round(2 + 8 * p.flow);
  const side = (i: number) => (i % 2 === 0 ? { x0: w.x0 + 10, x1: cut.x0 - 8 } : { x0: cut.x1 + 8, x1: w.x1 - 10 });
  for (let i = 0; i < N; i++) {
    const s = side(i);
    const len = r.range(8, 18), ux = r.next(), uy = r.next();
    if (i >= n || s.x1 - s.x0 < len + 4) continue;
    const x = s.x0 + ux * (s.x1 - s.x0 - len), y = w.y0 + 5 + uy * Math.max(2, w.y1 - w.y0 - 9);
    if (y > w.y1 - 2) continue;
    L.path(new Path().M(x, y).Q(x + len / 2, y - 1.6, x + len, y), { stroke: sw.water.sheetLight, sw: 1.2, so: 0.7, part: 'ripple-mark' });
    if (ctx.detail.minorRepairs) L.path(new Path().M(x + 3, y + 2.2).Q(x + len / 2 + 2, y + 1, x + len - 3, y + 2.2), { stroke: sw.water.deep, sw: 1, so: 0.3, part: 'ripple-shade' });
  }
}

function waterlineFoam(ctx: Ctx, L: Layer): void {
  const { L: lay, sw, p } = ctx;
  const cut = waterCut(lay), w = lay.water;
  const r = ctx.rng('foam.waterline');
  const so = 0.55 + 0.4 * p.flow;
  const pts: Pt[] = [];
  for (let x = cut.x0 + 1; x <= cut.x1 - 1; x += 6) pts.push({ x, y: cut.y + 1.1 + r.range(-0.5, 0.5) });
  L.path(new Path().poly(pts, false), { stroke: sw.water.foam, sw: 1.7, so, part: 'waterline-foam' });
  L.line(w.x0 + w.r, w.y1 - 0.8, w.x1 - w.r, w.y1 - 0.8, { stroke: sw.water.sheetLight, sw: 1, so: 0.5, part: 'near-rim-glint' });
}

export function paintFoam(ctx: Ctx, L: Layer): void {
  const { L: lay, p } = ctx;
  if (lay.water.present) { waterlineFoam(ctx, L); rippleMarks(ctx, L); }
  if (p.flow <= 0) return;
  if (lay.water.present) rippleRings(ctx, L);
  for (const t of lay.tiers) foamCluster(ctx, L, t);
}

/** Optional spray: finite droplets around landings. Skipped (layer empty) below flow 0.2. */
export function paintSpray(ctx: Ctx, L: Layer): void {
  const { L: lay, sw, p } = ctx;
  if (p.flow < 0.2) return;
  const scale = ctx.detail.tier === 'readable' ? 0.5 : 1;
  for (const t of lay.tiers) {
    const c = landingPoint(t);
    const r = ctx.rng(`spray.${t.k}`);
    const n = Math.round(p.flow * (t.k === 0 ? 9 : 5) * scale);
    for (let i = 0; i < 9; i++) {
      const dx = r.range(-14, 14) * (t.k === 0 ? 1.3 : 1), dy = -r.range(3, t.k === 0 ? 17 : 12), rr = r.range(0.8, 1.9);
      const lift = ctx.phase === undefined ? 0 : Math.sin(ctx.phase * Math.PI) * 2;
      if (i >= n) continue;
      L.circle(c.x + dx, c.y + dy - lift, rr, { fill: sw.water.foam, fo: 0.88, stroke: sw.water.foamShade, sw: 0.5, so: 0.7, content: false, part: 'spray' });
    }
  }
}
